import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { get, post, remove } from 'src/api/requests';
import { useEnvironment } from './EnvironmentContext';
import { useSessionStorageState } from 'ahooks';
import { getAuth } from 'firebase/auth';
import { Loading } from 'src/components/Loading';

export type ServiceModeContext = {
  userId: string;
  envId: string;
};

type ServiceModeContextType = {
  generateServiceKey: (environmentId: string, orgId: string) => Promise<string>;
  revokeServiceKey: () => Promise<void>;
  serviceKey?: string;
  overrideEnvId?: string;
};

type ServiceModeProviderProps = {
  children: React.ReactNode;
};

const ServiceModeContext = createContext<ServiceModeContextType>(
  {} as ServiceModeContextType,
);

type ApiKeyResponse = {
  id: string;
  apiKey: string;
  environment: { id: string };
};

export const ServiceModeProvider = ({ children }: ServiceModeProviderProps) => {
  const [serviceKeyId, setServiceKeyId] = useSessionStorageState<string>(
    '@control-plane/service-key-id',
    {
      defaultValue: '',
      listenStorageChange: true,
    },
  );
  const [serviceKey, setServiceKey] = useState<string>();
  const [overrideEnvId, setOverrideEnvId] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const { environment } = useEnvironment();
  const auth = getAuth();

  const fetchServiceKey = useCallback(
    async (serviceKeyId: string) => {
      try {
        setLoading(true);
        const data: ApiKeyResponse = await get(`/api-key/${serviceKeyId}`, {
          envId: environment?.envId,
        });
        setServiceKey(data.apiKey);
        setOverrideEnvId(data.environment.id);
      } finally {
        setLoading(false);
      }
    },
    [environment?.envId],
  );

  useEffect(() => {
    if (!serviceKeyId) {
      return;
    }
    if (!auth.currentUser) {
      return;
    }
    fetchServiceKey(serviceKeyId);
  }, [serviceKeyId, auth.currentUser]);

  const generateServiceKey = async (environmentId: string, orgId: string) => {
    // Only allow one at a time
    if (serviceKeyId) {
      await revokeServiceKey();
    }
    const data: ApiKeyResponse = await post(
      `/api-key`,
      { environmentId, orgId },
      {
        envId: environment?.envId,
      },
    );

    setServiceKey(data.apiKey);
    setServiceKeyId(data.id);
    return data.apiKey;
  };

  const revokeServiceKey = async () => {
    if (!serviceKeyId) {
      return;
    }
    await remove(`/api-key/${serviceKeyId}`, {
      envId: environment?.envId,
    });
    setServiceKey(undefined);
    setServiceKeyId(undefined);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <ServiceModeContext.Provider
      value={{
        generateServiceKey,
        revokeServiceKey,
        serviceKey,
        overrideEnvId,
      }}
    >
      {children}
    </ServiceModeContext.Provider>
  );
};

export const useServiceMode = () => {
  return useContext(ServiceModeContext);
};
